import { Box, Dialog, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import { Icon } from "./icons";
import { g } from "./tokens";

const fall = keyframes`
  from { transform: translateY(-12px) rotate(0); opacity: 0; }
  30% { opacity: 1; }
  to { transform: translateY(160px) rotate(360deg); opacity: 0; }
`;

const confettiColors = [
  "#F2DE3A",
  "#014638",
  "#2a6a55",
  "#cbdcc6",
  "#FBE7DF",
  "#7C86FF",
];

export const Confetti = ({ count = 24 }: { count?: number }) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 600,
        color: confettiColors[i % confettiColors.length],
        size: 6 + Math.random() * 6,
        rot: Math.random() * 360,
      })),
    [count],
  );
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        borderRadius: g.radiusLg,
      }}
    >
      {pieces.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: -10,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.4,
            background: p.color,
            transform: `rotate(${p.rot}deg)`,
            borderRadius: "1px",
            opacity: 0,
            animation: `${fall} 1500ms ${p.delay}ms cubic-bezier(.4,.2,.6,1) forwards`,
          }}
        />
      ))}
    </Box>
  );
};

type CalloutTone = "yellow" | "green" | "danger";

const calloutToneSx: Record<CalloutTone, object> = {
  yellow: {
    background: g.yellowBg,
    color: g.yellowText,
    border: `1px solid ${g.yellowBorder}`,
  },
  green: {
    background: g.primaryTint,
    color: g.primary,
    border: `1px solid ${g.primaryTintBorder}`,
  },
  danger: {
    background: g.dangerBg,
    color: g.danger,
    border: `1px solid ${g.dangerBorder}`,
  },
};

export const Callout = ({
  tone = "yellow",
  icon,
  children,
}: {
  tone?: CalloutTone;
  icon?: JSX.Element;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      gap: "12px",
      alignItems: "flex-start",
      px: "16px",
      py: "14px",
      borderRadius: g.radiusMd,
      fontSize: "14px",
      lineHeight: 1.45,
      ...calloutToneSx[tone],
      "& svg": { flexShrink: 0, mt: "1px" },
    }}
  >
    {icon}
    <Box>{children}</Box>
  </Box>
);

export type WeekStatus = "done" | "now" | "locked" | "missed";

export const WeekCard = ({
  label,
  value,
  total,
  status,
}: {
  label: string;
  value: number | null;
  total: number;
  status: WeekStatus;
}) => {
  const isLocked = status === "locked";
  const isMissed = status === "missed";
  const isDone = status === "done";
  const isNow = status === "now";

  const bg = isDone
    ? g.green50
    : isMissed
      ? g.dangerBg
      : isLocked
        ? "#f7f3e5"
        : g.cardBg;
  const border = isNow
    ? `1.5px solid ${g.primary}`
    : `1px solid ${isDone ? g.greenBorder : isMissed ? g.dangerBorder : g.border}`;
  const numColor = isMissed ? g.danger : isLocked ? g.textDisabled : g.primary;
  const labelColor = isMissed ? g.danger : isLocked ? g.textDisabled : g.text;

  return (
    <Box
      sx={{
        borderRadius: g.radiusMd,
        p: "14px 12px 12px",
        textAlign: "center",
        background: bg,
        border,
        minHeight: 96,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: labelColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
      {isLocked ? (
        <Box
          sx={{
            mt: "10px",
            color: g.textDisabled,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Icon.Lock size={16} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              fontSize: 28,
              fontWeight: 800,
              lineHeight: 1.1,
              color: numColor,
              mt: "2px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
          >
            {value}
          </Box>
          <Typography
            sx={{
              fontSize: 11,
              color: isMissed ? g.danger : g.text3,
              mt: "2px",
            }}
          >
            / {total} sent
          </Typography>
        </>
      )}
    </Box>
  );
};

export const SectionHead = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: object;
}) => (
  <Typography
    sx={{
      fontSize: 11,
      letterSpacing: "0.14em",
      fontWeight: 700,
      color: g.text3,
      textTransform: "uppercase",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const ModulePanel = ({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: object;
}) => (
  <Box
    sx={{
      background: g.panelBg,
      border: `1px solid ${g.border}`,
      borderRadius: g.radiusXl,
      p: { xs: "20px", md: "28px 28px 26px" },
      position: "relative",
      overflow: "hidden",
      maxWidth: 1300,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const ModuleHeader = ({
  eyebrow,
  title,
  sub,
  right,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  right?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: { xs: 2, md: 3 },
      mb: "18px",
      flexDirection: { xs: "column", sm: "row" },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      {eyebrow && <SectionHead sx={{ mb: "8px" }}>{eyebrow}</SectionHead>}
      <Typography
        component="h2"
        sx={{
          m: 0,
          fontSize: { xs: 20, md: 24 },
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          fontWeight: 800,
          color: g.text,
        }}
      >
        {title}
      </Typography>
      {sub && (
        <Typography
          sx={{ mt: "8px", fontSize: 14, lineHeight: 1.5, color: g.text2 }}
        >
          {sub}
        </Typography>
      )}
    </Box>
    {right && <Box sx={{ flexShrink: 0 }}>{right}</Box>}
  </Box>
);

type PillVariant = "default" | "yellow" | "danger" | "solid";

const pillVariantSx: Record<PillVariant, object> = {
  default: { background: g.primaryTint, color: g.primary },
  yellow: { background: g.yellowBg, color: g.yellowText },
  danger: { background: g.dangerBg, color: g.danger },
  solid: { background: g.primary, color: "#fff" },
};

export const Pill = ({
  variant = "default",
  icon,
  children,
  sx,
}: {
  variant?: PillVariant;
  icon?: JSX.Element;
  children: React.ReactNode;
  sx?: object;
}) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      px: "10px",
      py: "4px",
      borderRadius: "999px",
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
      ...pillVariantSx[variant],
      ...sx,
    }}
  >
    {icon}
    {children}
  </Box>
);

export const ProgressBar = ({ value }: { value: number }) => (
  <Box
    sx={{
      height: 8,
      background: g.progressTrack,
      borderRadius: "999px",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        height: "100%",
        width: `${Math.min(100, Math.max(0, value))}%`,
        background: `linear-gradient(90deg, ${g.yellowBarFrom}, ${g.yellowBarTo})`,
        borderRadius: "999px",
        transition: "width 360ms cubic-bezier(.2,.7,.2,1)",
      }}
    />
  </Box>
);

export const HowItWorksModal = ({
  open,
  onClose,
  target = 30,
}: {
  open: boolean;
  onClose: () => void;
  target?: number;
}) => {
  const navigate = useNavigate();
  const steps = [
    {
      n: 1,
      title: `Send ${target} pitches every week`,
      desc: "A pitch counts the moment you create it in Bento — not when it's actually sent out. Hit the weekly goal to lock that week in.",
      icon: <Icon.Send size={18} />,
    },
    {
      n: 2,
      title: "Repeat for 4 consecutive weeks",
      desc: "Weeks reset at Sunday midnight UTC. Miss a week and your streak resets — but you can restart anytime.",
      icon: <Icon.Flame size={18} />,
    },
    {
      n: 3,
      title: "$30 site credit auto-applied",
      desc: "No claim button. The credit lands on your next invoice automatically once you finish your first 4-week streak.",
      icon: <Icon.Dollar size={18} />,
    },
    {
      n: 4,
      title: "After that — keep pitching",
      desc: "The $30 is a one-time reward. The real payoff is the brand replies. We'll keep tracking your streak so you can see your momentum.",
      icon: <Icon.TrendUp size={18} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          p: "20px 24px 16px",
          background: `linear-gradient(135deg, ${g.primary} 0%, #1c5340 100%)`,
          color: "#fff",
          position: "relative",
        }}
      >
        <Box
          component="button"
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 28,
            height: 28,
            borderRadius: "999px",
            border: 0,
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          <Icon.X size={14} />
        </Box>
        <SectionHead sx={{ color: g.yellow }}>
          How the challenge works
        </SectionHead>
        <Typography
          sx={{
            mt: "6px",
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "-0.015em",
            color: "white",
          }}
        >
          4 weeks of consistency → $30 in your Bento account
        </Typography>
        <Typography
          sx={{
            mt: "8px",
            fontSize: 13,
            color: "rgba(255,255,255,0.78)",
            lineHeight: 1.5,
          }}
        >
          One-time reward. Designed to get you past the first month — where most
          creators quit. Weeks are on the calendar; pre-subscription pitches in
          the current week already count.
        </Typography>
      </Box>

      {/* Steps */}
      <Box
        sx={{
          p: "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        {steps.map((s) => (
          <Box key={s.n} sx={{ display: "flex", gap: "14px" }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: g.primaryTint,
                color: g.primary,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                fontWeight: 800,
                position: "relative",
              }}
            >
              <Box component="span" sx={{ fontSize: 14, fontWeight: 800 }}>
                {s.n}
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: -6,
                  right: -6,
                  width: 20,
                  height: 20,
                  borderRadius: "999px",
                  background: "#fff",
                  border: `1px solid ${g.border}`,
                  display: "grid",
                  placeItems: "center",
                  color: g.primary,
                }}
              >
                {s.icon}
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: g.text }}>
                {s.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: g.text2,
                  mt: "2px",
                  lineHeight: 1.5,
                }}
              >
                {s.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: "16px 24px",
          borderTop: `1px solid ${g.border}`,
          background: "#fbf9f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: 13, color: g.text2 }}>
          Want to share your progress?{" "}
          <Box
            component="a"
            href="https://instagram.com/onbento"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: g.text,
              fontWeight: 700,
              textDecoration: "underline",
            }}
          >
            tag @onbento
          </Box>
        </Typography>
        <Box
          component="button"
          onClick={() => {
            onClose();
            navigate(`/${routes.brands}/all`);
          }}
          sx={{
            background: g.primary,
            color: "#fff",
            border: 0,
            borderRadius: "10px",
            px: "20px",
            py: "12px",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          Got it — start pitching
          <Icon.ArrowRight size={16} />
        </Box>
      </Box>
    </Dialog>
  );
};
